import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { toast } from 'sonner';
import { shareToTwitter } from '../../services/shareService';
import { getReferralStats } from '../../api/referrals';
import { shortKey } from '../../utils/format';
import { XTwitterIcon } from './icons';
import { ArrowDownTrayIcon, LinkIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SeasonShareCardProps {
  seasonName: string;
  rank: number;
  points: number;
  matchesPlayed: number;
  matchesWon: number;
  multiplier?: number;
  onClose: () => void;
}

function generateSeasonImage(
  props: SeasonShareCardProps & { referralCode?: string },
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 630;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve(null);
      return;
    }

    // Background
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#0a0a1a');
    gradient.addColorStop(0.5, '#1a0a2e');
    gradient.addColorStop(1, '#0a0a1a');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Border
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 3;
    ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 10);

    // SOLANTE header
    ctx.font = 'bold 32px monospace';
    ctx.fillStyle = '#00ffff';
    ctx.textAlign = 'center';
    ctx.fillText('SOLANTE', canvas.width / 2, 70);

    // Season name
    ctx.font = 'bold 24px monospace';
    ctx.fillStyle = '#ff10f0';
    ctx.fillText(props.seasonName.toUpperCase(), canvas.width / 2, 110);

    // Rank (big)
    ctx.font = 'bold 120px monospace';
    ctx.fillStyle = '#00ffff';
    ctx.fillText(`#${props.rank}`, canvas.width / 2, 280);

    // Points
    ctx.font = 'bold 36px monospace';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(`${Math.floor(props.points).toLocaleString()} POINTS`, canvas.width / 2, 340);

    // Stats row
    ctx.font = '24px monospace';
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    const winRate =
      props.matchesPlayed > 0 ? Math.round((props.matchesWon / props.matchesPlayed) * 100) : 0;
    ctx.fillText(
      `${props.matchesWon}W / ${props.matchesPlayed - props.matchesWon}L  •  ${winRate}% Win Rate`,
      canvas.width / 2,
      390,
    );

    // Multiplier badge
    if (props.multiplier && props.multiplier > 1) {
      ctx.font = 'bold 20px monospace';
      ctx.fillStyle = '#fbbf24';
      ctx.fillText(`${props.multiplier}x SEASON MULTIPLIER`, canvas.width / 2, 440);
    }

    // Referral code
    if (props.referralCode) {
      ctx.font = '18px monospace';
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.fillText(
        `Join: ${window.location.origin}?ref=${props.referralCode}`,
        canvas.width / 2,
        560,
      );
    }

    // Branding
    ctx.font = '16px monospace';
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillText('solante.gg', canvas.width / 2, 600);

    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

export const SeasonShareCard: React.FC<SeasonShareCardProps> = (props) => {
  const { publicKey } = useWallet();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (publicKey) {
      getReferralStats(publicKey.toBase58())
        .then((s) => setReferralCode(s.referralCode))
        .catch(() => {});
    }
  }, [publicKey]);

  const shareUrl = referralCode
    ? `${window.location.origin}?ref=${referralCode}`
    : window.location.origin;

  const handleShare = () => {
    const winRate =
      props.matchesPlayed > 0 ? Math.round((props.matchesWon / props.matchesPlayed) * 100) : 0;
    const text = `Ranked #${props.rank} in ${props.seasonName} on Solante! ${Math.floor(props.points).toLocaleString()} points | ${winRate}% win rate | ${props.matchesWon} wins`;
    shareToTwitter(text, shareUrl);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await generateSeasonImage({ ...props, referralCode: referralCode ?? undefined });
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `solante-season-${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Image downloaded!');
      }
    } catch {
      toast.error('Failed to download image');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        toast.success('Link copied!');
      })
      .catch(() => {
        toast.error('Failed to copy');
      });
  };

  const winRate =
    props.matchesPlayed > 0 ? Math.round((props.matchesWon / props.matchesPlayed) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={props.onClose}
    >
      <motion.div
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        className="w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Card Preview */}
        <div className="arcade-panel pixel-corners overflow-hidden rounded-xl">
          <div className="bg-gradient-to-br from-[#0a0a1a] via-[#1a0a2e] to-[#0a0a1a] p-6">
            <div className="mb-1 text-center font-display text-xs uppercase tracking-wider text-arcade-cyan">
              SOLANTE
            </div>
            <div className="mb-4 text-center font-display text-sm uppercase text-arcade-pink">
              {props.seasonName}
            </div>

            <div className="mb-2 text-center font-display text-4xl sm:text-5xl font-bold text-arcade-cyan">
              #{props.rank}
            </div>
            <div className="mb-4 text-center font-display text-xl text-white">
              {Math.floor(props.points).toLocaleString()} pts
            </div>

            <div className="flex justify-center gap-6 font-pixel text-xs text-white/60">
              <span>
                <span className="text-arcade-green">{props.matchesWon}W</span> /{' '}
                <span className="text-arcade-pink">{props.matchesPlayed - props.matchesWon}L</span>
              </span>
              <span>{winRate}% WR</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex gap-2">
          <button
            onClick={handleShare}
            className="btn-press flex flex-1 items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/10 py-3 font-pixel text-xs text-white transition hover:bg-white/20"
          >
            <XTwitterIcon className="h-4 w-4" />
            Share
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="btn-press flex flex-1 items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/10 py-3 font-pixel text-xs text-white transition hover:bg-white/20 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {downloading ? 'Saving...' : 'Download'}
          </button>
          <button
            onClick={handleCopyLink}
            className="btn-press flex items-center justify-center rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white transition hover:bg-white/20"
          >
            <LinkIcon className="h-4 w-4" />
          </button>
          <button
            onClick={props.onClose}
            className="flex items-center justify-center rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white/50 transition hover:bg-white/20 hover:text-white"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
